import { type CSSProperties, FC, useEffect, useState } from 'react'

import * as Icons from '@icons'
import DashboardBanner from '@components/molecules/dashboard-banner'
import './new-user-landing.css'


type BannerVariant = 'promo' | 'referral' | 'welcome'

interface FeedRow {
    label: string
    amount: string
}

interface Slide {
    accentColor: string
    membersLabel: string
    statement: string
    feed: FeedRow[]
}

interface Props {
    bannerVariant: BannerVariant
    promoAmount?: number
    groupSpendRequirement?: number
    participantCountRequirement?: number
    onBannerClick?: () => void
    onCreateGroup: () => void
    hideCreateGroup?: boolean
    onTryDemo?: () => void
}

function splitCurrencyAmount(amount: string) {
    if (!amount.startsWith('$')) return { symbol: '', value: amount }

    return {
        symbol: '$',
        value: amount.slice(1),
    }
}

const slideIntervalMs = 3750
const slides: Slide[] = [
    {
        accentColor: '#E50914',
        membersLabel: '4 members splitting',
        statement: 'Split your streaming.',
        feed: [
            { label: 'Netflix', amount: '$4.00' },
            { label: 'HBO', amount: '$4.00' },
            { label: 'Disney+', amount: '$3.50' },
        ],
    },
    {
        accentColor: '#4ADE80',
        membersLabel: '3 roommates chipping in',
        statement: 'Split with roommates.',
        feed: [
            { label: 'Rent', amount: '$162.50' },
            { label: 'Utilities', amount: '$30.00' },
            { label: 'Groceries', amount: '$21.25' },
        ],
    },
    {
        accentColor: '#FF5A5F',
        membersLabel: '5 friends on one trip',
        statement: 'Split your trip.',
        feed: [
            { label: 'Flights', amount: '$95.00' },
            { label: 'Hotel', amount: '$120.00' },
            { label: 'Rides', amount: '$16.00' },
        ],
    },
    {
        accentColor: '#A855F7',
        membersLabel: '4 friends going out',
        statement: 'Split your night out.',
        feed: [
            { label: 'Dinner', amount: '$16.95' },
            { label: 'Drinks', amount: '$11.25' },
            { label: 'Uber', amount: '$7.13' },
        ],
    },
]

const bannerVariantMap: Record<BannerVariant, 'promo' | 'creditAmount' | 'welcome'> = {
    promo: 'promo',
    referral: 'creditAmount',
    welcome: 'welcome',
}

const memberAvatars = [
    { initials: 'JM', color: '#3B82F6' },
    { initials: 'SR', color: '#A855F7' },
    { initials: 'KL', color: '#10B981' },
    { initials: 'DT', color: '#F59E0B' },
]

const NewUserLanding: FC<Props> = ({
    bannerVariant,
    promoAmount,
    groupSpendRequirement,
    participantCountRequirement,
    hideCreateGroup = false,
    onBannerClick,
    onCreateGroup,
    onTryDemo,
}) => {
    const demoCompleted = (() => { try { return localStorage.getItem('demo_completed') === 'true' } catch { return false } })()
    const [slideIndex, setSlideIndex] = useState(0)
    const activeSlide = slides[slideIndex]

    useEffect(() => {
        const interval = window.setInterval(() => {
            setSlideIndex((currentIndex) => (currentIndex + 1) % slides.length)
        }, slideIntervalMs)

        return () => window.clearInterval(interval)
    }, [])

    return (
        <div className='new-user-landing'>
            <DashboardBanner
                variant={bannerVariantMap[bannerVariant]}
                promoAmount={promoAmount}
                groupSpendRequirement={groupSpendRequirement}
                participantCountRequirement={participantCountRequirement}
                onClick={onBannerClick}
            />

            <section className='new-user-landing-hero'>
                <h1 className='new-user-landing-headline'>
                    <span className='new-user-landing-headline-line'>One card.</span>
                    <span
                        className='new-user-landing-headline-line new-user-landing-headline-highlight'
                        style={{ '--landing-accent-color': activeSlide.accentColor } as CSSProperties}
                    >
                        {activeSlide.statement}
                    </span>
                    <span className='new-user-landing-headline-line'>See everything.</span>
                </h1>

                <p className='new-user-landing-subtitle'>
                    Create a virtual Mastercard, invite your crew, and track every dollar in real time.
                </p>

                <div className='new-user-landing-visual'>
                    <div className='new-user-landing-card'>
                        <div className='new-user-landing-card-top'>
                            <div className='new-user-landing-card-chip' />
                            <div className='new-user-landing-card-mark'>SPLITCARD</div>
                        </div>
                        <div className='new-user-landing-card-digits'>
                            <span className='new-user-landing-card-digits-mask'>.... .... ....</span>
                            <span className='new-user-landing-card-digits-last4'>4821</span>
                        </div>
                        <div className='new-user-landing-card-brand'>
                            <span className='new-user-landing-card-circle new-user-landing-card-circle-red' />
                            <span className='new-user-landing-card-circle new-user-landing-card-circle-orange' />
                        </div>
                    </div>

                    <div className='new-user-landing-feed'>
                        {activeSlide.feed.map((row) => {
                            const { symbol, value } = splitCurrencyAmount(row.amount)

                            return (
                                <div key={`${activeSlide.statement}-${row.label}`} className='new-user-landing-feed-row'>
                                    <span
                                        className='new-user-landing-feed-dot'
                                        style={{ backgroundColor: activeSlide.accentColor }}
                                    />
                                    <span className='new-user-landing-feed-label'>{row.label}</span>
                                    <span className='new-user-landing-feed-amount'>
                                        <span className='new-user-landing-feed-currency'>{symbol}</span>
                                        <span className='new-user-landing-feed-value'>{value}</span>
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div className='new-user-landing-members'>
                    <div className='new-user-landing-members-stack'>
                        {memberAvatars.map((member, index) => (
                            <span
                                key={member.initials}
                                className='new-user-landing-member-avatar'
                                style={{ backgroundColor: member.color, zIndex: memberAvatars.length - index }}
                            >
                                {member.initials}
                            </span>
                        ))}
                    </div>
                    <span className='new-user-landing-members-label'>{activeSlide.membersLabel}</span>
                </div>

                {!hideCreateGroup && (
                    <button type='button' className='new-user-landing-cta' onClick={onCreateGroup}>
                        <span>Get Started - It&apos;s Free</span>
                        <Icons.Arrow width='1rem' height='1rem' facing='right' strokeWidth='2px' />
                    </button>
                )}

                {onTryDemo && (
                    <button type='button' className='new-user-landing-demo-cta' onClick={onTryDemo}>
                        <span>{demoCompleted ? 'Watch Again' : 'See How It Works'}</span>
                    </button>
                )}
            </section>
        </div>
    )
}

export default NewUserLanding
